import { Apple, Play, Smartphone } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import CivicPointsIconLogo from '../../assets/icons/civicpoints_logo.svg';

const IOS_LINK = 'https://apps.apple.com/vn/app/civicpoints/id6747657300';
const ANDROID_LINK = 'https://play.google.com/store/apps/details?id=com.idlemindtech.cityeg.prod&pcampaignid=web_share';

const AppDownloadPage: React.FC = () => {
    const [showFallback, setShowFallback] = useState(false);

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    useEffect(() => {
        if (isIOS) {
            window.location.href = IOS_LINK;
            return;
        }
        if (isAndroid) {
            window.location.href = ANDROID_LINK;
            return;
        }
        setShowFallback(true);
    }, []);

    const handleIOSDownload = () => {
        window.open(IOS_LINK, '_blank');
    };

    const handleAndroidDownload = () => {
        window.open(ANDROID_LINK, '_blank');
    };

    // Desktop/other: show fallback page with manual buttons
    if (!showFallback) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-[#0d2721] dark:via-[#1a1a1a] dark:to-[#0d2721] p-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00C49A] mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-300">Redirecting to store...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-[#0d2721] dark:via-[#1a1a1a] dark:to-[#0d2721] p-4">
            <div className="w-full max-w-md">
                <div className="bg-white dark:bg-[#242424] rounded-2xl shadow-xl p-8 md:p-10">
                    {/* Logo */}
                    <div className="flex justify-center mb-6">
                        <img
                            src={CivicPointsIconLogo}
                            alt="CivicPoints"
                            className="h-20 w-auto"
                        />
                    </div>

                    {/* Heading */}
                    <div className="text-center mb-8">
                        <h1 className="text-2xl md:text-3xl font-bold text-black dark:text-[#e5e5e5] mb-3">
                            Download CivicPoints
                        </h1>
                        <p className="text-base md:text-lg text-[#797979] dark:text-[#a0a0a0]">
                            Join community activities, earn points, and redeem rewards
                        </p>
                    </div>

                    {/* App Info Card */}
                    <div className="bg-[#eef9ef] dark:bg-[#0d2721] rounded-xl p-6 mb-8">
                        <div className="flex items-center gap-4">
                            <div className="bg-[#00C49A] rounded-xl p-4">
                                <Smartphone className="w-8 h-8 text-white" />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-lg font-bold text-[#004231] dark:text-[#00c49a] mb-1">
                                    CivicPoints App
                                </h2>
                                <p className="text-sm text-[#797979] dark:text-[#a0a0a0]">
                                    Your all-in-one platform for civic engagement
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Download Buttons */}
                    <div className="space-y-4">
                        <button
                            onClick={handleIOSDownload}
                            className="
                                w-full h-14 md:h-16
                                bg-black hover:bg-gray-800 dark:bg-[#2a2a2a] dark:hover:bg-[#3a3a3a]
                                text-white font-bold text-base md:text-lg
                                rounded-[30px]
                                focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 dark:focus:ring-gray-600
                                transition-all duration-200
                                transform hover:scale-[1.02] active:scale-[0.98]
                                flex items-center justify-center gap-3
                                shadow-lg hover:shadow-xl
                            "
                        >
                            <Apple className="w-6 h-6" />
                            <span>Download for iOS</span>
                        </button>

                        <button
                            onClick={handleAndroidDownload}
                            className="
                                w-full h-14 md:h-16
                                bg-[#00C49A] hover:bg-[#00b38a] dark:hover:bg-[#00d4a8]
                                text-[#004231] dark:text-[#004231]
                                font-bold text-base md:text-lg
                                rounded-[30px]
                                focus:outline-none focus:ring-2 focus:ring-[#00c49a] focus:ring-offset-2
                                transition-all duration-200
                                transform hover:scale-[1.02] active:scale-[0.98]
                                flex items-center justify-center gap-3
                                shadow-lg hover:shadow-xl
                            "
                        >
                            <Play className="w-6 h-6" />
                            <span>Download for Android</span>
                        </button>
                    </div>

                    {/* Features List */}
                    <div className="mt-8 pt-8 border-t border-[#dcdcdc] dark:border-[#404040]">
                        <h3 className="text-sm font-semibold text-[#75757d] dark:text-[#a0a0a0] mb-4 text-center">
                            Key Features
                        </h3>
                        <ul className="space-y-3 text-sm text-[#797979] dark:text-[#a0a0a0]">
                            <li className="flex items-start gap-2">
                                <span className="text-[#00C49A] mt-1">✓</span>
                                <span>City News & Alerts – Get real-time updates from your city</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-[#00C49A] mt-1">✓</span>
                                <span>Attend Local Events – Discover and attend community events</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-[#00C49A] mt-1">✓</span>
                                <span>Complete Surveys – Share your opinions on local issues</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-[#00C49A] mt-1">✓</span>
                                <span>Earn Rewards – Get points and redeem gift cards</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppDownloadPage;
